// src/hooks/useLabAnalytics.ts

import {
  useCallback,
} from "react";

import type {
  CompletedTest,
  OngoingTest,
  TestPriority,
  TestRequest,
  TestStatistics,
} from "../types/lab-context.types";

interface UseLabAnalyticsOptions {
  testRequests: TestRequest[];
  ongoingTests: OngoingTest[];
  completedTests: CompletedTest[];
}

interface UseLabAnalyticsReturn {
  filterTestsByDateRange: (
    dateRange: string,
  ) => {
    requests: TestRequest[];
    ongoing: OngoingTest[];
    completed: CompletedTest[];
  };

  getTestStatistics: (
    dateRange?: string,
  ) => TestStatistics;

  getMonthlyTestVolume: (
    dateRange?: string,
  ) => Array<{
    month: string;
    tests: number;
  }>;

  getTestTypeDistribution: () => Array<{
    name: string;
    count: number;
    percentage: number;
  }>;

  getTurnaroundTimeDistribution: () => Array<{
    range: string;
    count: number;
    percentage: number;
  }>;

  getTopDoctors: (
    limit?: number,
  ) => Array<{
    name: string;
    tests: number;
    hospital: string;
  }>;

  getTopHospitals: (
    limit?: number,
  ) => Array<{
    name: string;
    tests: number;
  }>;

  searchTests: (
    query: string,
  ) => {
    requests: TestRequest[];
    ongoing: OngoingTest[];
    completed: CompletedTest[];
  };

  filterByPriority: (
    priority: TestPriority,
  ) => {
    requests: TestRequest[];
    ongoing: OngoingTest[];
  };
}

/**
 * Safely converts a turnaround-time string such as
 * "4h 30m" into total minutes.
 */
const getTurnaroundMinutes = (
  turnaroundTime: string,
): number => {
  const hoursMatch =
    turnaroundTime.match(/(\d+)\s*h/i);

  const minutesMatch =
    turnaroundTime.match(/(\d+)\s*m/i);

  const hours = hoursMatch
    ? Number(hoursMatch[1])
    : 0;

  const minutes = minutesMatch
    ? Number(minutesMatch[1])
    : 0;

  return hours * 60 + minutes;
};

/**
 * Returns a safe date value for sorting and filtering.
 */
const getDateTimestamp = (
  value: string,
): number => {
  const timestamp =
    new Date(value).getTime();

  return Number.isNaN(timestamp)
    ? 0
    : timestamp;
};

 //Laboratory analytics and cross-module query helpers.
 //This hook derives values from the provided collections and does
 // not own or mutate laboratory state.
 
export const useLabAnalytics = ({
  testRequests,
  ongoingTests,
  completedTests,
}: UseLabAnalyticsOptions): UseLabAnalyticsReturn => {
  const filterTestsByDateRange =
    useCallback(
      (
        dateRange: string,
      ) => {
        const now = new Date();

        let startDate: Date | null =
          null;

        switch (dateRange) {
          case "today": {
            startDate = new Date(now);
            startDate.setHours(
              0,
              0,
              0,
              0,
            );
            break;
          }

          case "week": {
            startDate = new Date(now);
            startDate.setDate(
              startDate.getDate() -
                7,
            );
            break;
          }

          case "month": {
            startDate = new Date(now);
            startDate.setMonth(
              startDate.getMonth() -
                1,
            );
            break;
          }

          case "quarter": {
            startDate = new Date(now);
            startDate.setMonth(
              startDate.getMonth() -
                3,
            );
            break;
          }

          default:
            return {
              requests: testRequests,
              ongoing: ongoingTests,
              completed:
                completedTests,
            };
        }

        const startTimestamp =
          startDate.getTime();

        return {
          requests:
            testRequests.filter(
              (test) =>
                getDateTimestamp(
                  test.requestDate,
                ) >= startTimestamp,
            ),

          ongoing:
            ongoingTests.filter(
              (test) =>
                getDateTimestamp(
                  test.startTime,
                ) >= startTimestamp,
            ),

          completed:
            completedTests.filter(
              (test) =>
                getDateTimestamp(
                  test.completedDate,
                ) >= startTimestamp,
            ),
        };
      },
      [
        completedTests,
        ongoingTests,
        testRequests,
      ],
    );

  const getTestStatistics =
    useCallback(
      (
        dateRange = "month",
      ): TestStatistics => {
        const filtered =
          filterTestsByDateRange(
            dateRange,
          );

        const requestCount =
          filtered.requests.length;

        const ongoingCount =
          filtered.ongoing.length;

        const completedCount =
          filtered.completed.length;

        const totalTests =
          requestCount +
          ongoingCount +
          completedCount;

        const pendingTests =
          requestCount +
          ongoingCount;

        const totalTurnaroundMinutes =
          filtered.completed.reduce(
            (
              total,
              test,
            ) =>
              total +
              getTurnaroundMinutes(
                test.turnaroundTime,
              ),
            0,
          );

        const averageMinutes =
          completedCount > 0
            ? totalTurnaroundMinutes /
              completedCount
            : 0;

        const averageHours =
          Math.floor(
            averageMinutes / 60,
          );

        const remainingMinutes =
          Math.floor(
            averageMinutes % 60,
          );

        const completionRate =
          totalTests > 0
            ? (completedCount /
                totalTests) *
              100
            : 0;

        return {
          totalTests,
          completedTests:
            completedCount,
          pendingTests,

          avgTurnaroundTime:
            `${averageHours}h ${remainingMinutes}m`,

          completionRate:
            Number(
              completionRate.toFixed(
                1,
              ),
            ),
        };
      },
      [filterTestsByDateRange],
    );

  const getMonthlyTestVolume =
    useCallback(
      (
        dateRange = "quarter",
      ) => {
        const filtered =
          filterTestsByDateRange(
            dateRange,
          );

        const monthlyTotals =
          new Map<
            string,
            number
          >();

        filtered.completed.forEach(
          (test) => {
            const date =
              new Date(
                test.completedDate,
              );

            if (
              Number.isNaN(
                date.getTime(),
              )
            ) {
              return;
            }

            const month =
              date.toLocaleString(
                "en",
                {
                  month: "short",
                },
              );

            monthlyTotals.set(
              month,
              (monthlyTotals.get(
                month,
              ) ?? 0) + 1,
            );
          },
        );

        return Array.from(
          monthlyTotals.entries(),
        ).map(
          ([month, tests]) => ({
            month,
            tests,
          }),
        );
      },
      [filterTestsByDateRange],
    );

  const getTestTypeDistribution =
    useCallback(() => {
      const allTests = [
        ...testRequests,
        ...ongoingTests,
        ...completedTests,
      ];

      const testTypeCounts =
        new Map<
          string,
          number
        >();

      allTests.forEach((test) => {
        testTypeCounts.set(
          test.testType,
          (testTypeCounts.get(
            test.testType,
          ) ?? 0) + 1,
        );
      });

      const total =
        allTests.length;

      return Array.from(
        testTypeCounts.entries(),
      )
        .map(
          ([name, count]) => ({
            name,
            count,

            percentage:
              total > 0
                ? Number(
                    (
                      (count /
                        total) *
                      100
                    ).toFixed(1),
                  )
                : 0,
          }),
        )
        .sort(
          (
            firstItem,
            secondItem,
          ) =>
            secondItem.count -
            firstItem.count,
        );
    }, [
      completedTests,
      ongoingTests,
      testRequests,
    ]);

  const getTurnaroundTimeDistribution =
    useCallback(() => {
      const distribution = [
        {
          range: "< 2 hours",
          count: 0,
        },
        {
          range: "2–4 hours",
          count: 0,
        },
        {
          range: "4–8 hours",
          count: 0,
        },
        {
          range: "> 8 hours",
          count: 0,
        },
      ];

      completedTests.forEach(
        (test) => {
          const minutes =
            getTurnaroundMinutes(
              test.turnaroundTime,
            );

          if (minutes < 120) {
            distribution[0].count +=
              1;
          } else if (
            minutes < 240
          ) {
            distribution[1].count +=
              1;
          } else if (
            minutes < 480
          ) {
            distribution[2].count +=
              1;
          } else {
            distribution[3].count +=
              1;
          }
        },
      );

      const total =
        completedTests.length;

      return distribution.map(
        ({
          range,
          count,
        }) => ({
          range,
          count,

          percentage:
            total > 0
              ? Number(
                  (
                    (count /
                      total) *
                    100
                  ).toFixed(1),
                )
              : 0,
        }),
      );
    }, [completedTests]);

  const getTopDoctors =
    useCallback(
      (
        limit = 5,
      ) => {
        const allTests = [
          ...testRequests,
          ...ongoingTests,
          ...completedTests,
        ];

        const doctorTotals =
          new Map<
            string,
            {
              name: string;
              tests: number;
              hospital: string;
            }
          >();

        allTests.forEach(
          (test) => {
            const existingDoctor =
              doctorTotals.get(
                test.requestedBy,
              );

            doctorTotals.set(
              test.requestedBy,
              {
                name:
                  test.requestedBy,

                tests:
                  (existingDoctor?.tests ??
                    0) + 1,

                hospital:
                  existingDoctor?.hospital ??
                  test.hospital,
              },
            );
          },
        );

        return Array.from(
          doctorTotals.values(),
        )
          .sort(
            (
              firstDoctor,
              secondDoctor,
            ) =>
              secondDoctor.tests -
              firstDoctor.tests,
          )
          .slice(
            0,
            limit,
          );
      },
      [
        completedTests,
        ongoingTests,
        testRequests,
      ],
    );

  const getTopHospitals =
    useCallback(
      (
        limit = 5,
      ) => {
        const allTests = [
          ...testRequests,
          ...ongoingTests,
          ...completedTests,
        ];

        const hospitalTotals =
          new Map<
            string,
            number
          >();

        allTests.forEach(
          (test) => {
            hospitalTotals.set(
              test.hospital,
              (hospitalTotals.get(
                test.hospital,
              ) ?? 0) + 1,
            );
          },
        );

        return Array.from(
          hospitalTotals.entries(),
        )
          .map(
            ([name, tests]) => ({
              name,
              tests,
            }),
          )
          .sort(
            (
              firstHospital,
              secondHospital,
            ) =>
              secondHospital.tests -
              firstHospital.tests,
          )
          .slice(
            0,
            limit,
          );
      },
      [
        completedTests,
        ongoingTests,
        testRequests,
      ],
    );

  const searchTests =
    useCallback(
      (
        query: string,
      ) => {
        const normalizedQuery =
          query
            .trim()
            .toLowerCase();

        const matchesQuery = (
          test: {
            id: string;
            patientName: string;
            patientId: string;
            testType: string;
            requestedBy: string;
            hospital: string;
          },
        ): boolean => {
          if (!normalizedQuery) {
            return true;
          }

          return [
            test.id,
            test.patientName,
            test.patientId,
            test.testType,
            test.requestedBy,
            test.hospital,
          ].some((value) =>
            value
              .toLowerCase()
              .includes(
                normalizedQuery,
              ),
          );
        };

        return {
          requests:
            testRequests.filter(
              matchesQuery,
            ),

          ongoing:
            ongoingTests.filter(
              matchesQuery,
            ),

          completed:
            completedTests.filter(
              matchesQuery,
            ),
        };
      },
      [
        completedTests,
        ongoingTests,
        testRequests,
      ],
    );

  const filterByPriority =
    useCallback(
      (
        priority: TestPriority,
      ) => {
        return {
          requests:
            testRequests.filter(
              (test) =>
                test.priority ===
                priority,
            ),

          ongoing:
            ongoingTests.filter(
              (test) =>
                test.priority ===
                priority,
            ),
        };
      },
      [
        ongoingTests,
        testRequests,
      ],
    );

  return {
    filterTestsByDateRange,

    getTestStatistics,
    getMonthlyTestVolume,
    getTestTypeDistribution,
    getTurnaroundTimeDistribution,
    getTopDoctors,
    getTopHospitals,

    searchTests,
    filterByPriority,
  };
};
